import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { navGroupLabel, navMeta } from '../../../layouts/navData';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useDeleteFlow } from '../../../components/confirm';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { SettingsDetailPage, type DetailDataAdapter } from '../../modules/SettingsDetailPage';
import { AssignModuleDialog } from './AssignModuleDialog';
import { TranslationsDialog } from './TranslationsDialog';
import { ImportMessagesDialog } from './ImportMessagesDialog';
import { MessagePreview } from './MessagePreview';
import {
  ChangeHistoryPanel, SameWordingPanel, UsedInPanel,
} from './MessageDetailTabs';
import { TranslationsTab } from './TranslationsTab';
import type { DetailExtraTab } from '../../modules/SettingsDetailPage';
import type { MessageDetail, MessageHistoryRow, Severity } from './types';
import { MESSAGE_FORM_NAME, messageLibraryConfig } from './messageLibraryConfig';
import {
  addTranslation, assignModule, createMessage, deleteMessage, fetchMessage,
  fetchMessageHistory, fetchMessages,
  fetchModules, fetchVocabularies, syncCatalog, updateMessage,
} from './api';
import {
  buildIndex, canEditKey, checksOf, compareMsid, isCompleteDraft, sameWordingOf,
  severityOf, sourceOf, statusOf, visibleChecks, visibleRows,
  type DraftRow, type MessageIndex,
} from './messageLibraryModel';
import { IMPORT_COLUMNS, type MessageRow, type ModuleOption, type Vocabularies } from './types';
import { templateRows } from './messageImport';
import { downloadCsv, toCsv } from '../../../utils/csv';
import './messageLibrary.css';

const PAGE_ID = 'messagelibrary';

/** `undefined` = the list; `null` = New; a string = that record. */
type DetailTarget = string | null | undefined;

/**
 * System › Message Library.
 *
 * The admin screen over the XAF-shared `dbo.MessageEditor` — ALPACA's Settings ›
 * System Management › Message Editor, rebuilt on the shared components (ADR-0017).
 *
 * What it adds over the legacy form:
 *  - **Module and BO are editable.** ALPACA declares both `[RuleRequiredField]`
 *    yet renders them `AllowEdit="False"`, and its seeder never sets them, so the
 *    rule is unreachable and all 390 live rows are unattributed. **Assign Module**
 *    fixes them in batches.
 *  - **Severity**, so a message can say which surface it belongs on. Legacy rows
 *    have none, so the grid shows a server-computed SUGGESTION — visibly marked as
 *    one — until an admin confirms it. Nothing is back-filled.
 *  - **Checks**, flagging duplicate keys, stray whitespace, positional
 *    placeholders, shared wording and missing modules.
 *  - **A race-safe MSID.** Legacy mints it with an unlocked `Max(MSID) + 1`.
 *
 * Built entirely from the shared components (standing rules 7-9, 12): FormTitle,
 * AppToolbar, AppDataGrid, GridSearch, SettingsDetailPage, useNotify, useConfirm,
 * useDeleteFlow. The audit log and the reason-for-change gate come free from
 * `auditFormName` + `formName` (standing rule 11).
 */
export function MessageLibraryPage() {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const runDelete = useDeleteFlow();

  const [rows, setRows] = useState<MessageRow[]>([]);
  const [modules, setModules] = useState<ModuleOption[]>([]);
  const [vocab, setVocab] = useState<Vocabularies>({ severities: [], actionTypes: [], languages: [] });
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [detail, setDetail] = useState<DetailTarget>(undefined);
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');
  const [assignOpen, setAssignOpen] = useState(false);
  const [translationsFor, setTranslationsFor] = useState<MessageRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  /** The open record in FULL — the grid row carries no translations or source
   *  files, and the reference tabs need both. */
  const [detailRecord, setDetailRecord] = useState<MessageDetail | null>(null);
  // Fetched WITH the record rather than when the tab opens, so the tab can show
  // an honest count. A badge that appears only after you click the tab is worse
  // than one that is right from the start.
  const [history, setHistory] = useState<MessageHistoryRow[]>([]);
  // Translations typed on a NEW message, held until the message exists to hang
  // them off. Cleared whenever the detail changes, so they can never leak from
  // an abandoned New onto the next record.
  const [draftTranslations, setDraftTranslations] = useState<DraftRow[]>([]);
  // ...and a ref holding the same rows. The adapter reads THIS, never the state.
  //
  // <SettingsDetailPage> re-runs its load effect whenever the adapter's identity
  // changes, and that effect re-prefills the form. An adapter memoised on
  // `draftTranslations` therefore got a new identity on every keystroke in a
  // staged translation, which wiped what had been typed into the message itself.
  // A ref keeps the adapter stable while still letting create() see the latest
  // rows.
  const draftTranslationsRef = useRef<DraftRow[]>([]);
  useEffect(() => { draftTranslationsRef.current = draftTranslations; }, [draftTranslations]);

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const [list, mods, v] = await Promise.all([
        fetchMessages(), fetchModules(), fetchVocabularies(),
      ]);
      setRows(list);
      setModules(mods);
      setVocab(v);
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // The reference tabs read the FULL record, which the list query does not carry.
  // Pulled out of the effect so the Translations tab can re-read the record after
  // it saves a row - the tab counts and the list's Translations column both come
  // from this fetch, so they would otherwise show a stale number.
  const reloadDetail = useCallback(async () => {
    if (typeof detail !== 'string') { setDetailRecord(null); setHistory([]); return; }
    const [record, rows] = await Promise.all([
      fetchMessage(detail),
      // A failed audit read must not cost the whole record - the history is a
      // reference panel, the record is the point of the page.
      fetchMessageHistory(detail).catch(() => [] as MessageHistoryRow[]),
    ]);
    setDetailRecord(record);
    setHistory(rows);
  }, [detail]);

  useEffect(() => { void reloadDetail(); }, [reloadDetail]);

  useEffect(() => { setDraftTranslations([]); }, [detail]);

  // Duplicate-key and same-wording counts, built once per load: computing them
  // per row would make rendering the grid quadratic.
  const index: MessageIndex = useMemo(() => buildIndex(rows), [rows]);

  const visible = useMemo(() => visibleRows(rows, search), [rows, search]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.oid)), [rows, selected]);

  /** The one row an action like Edit applies to. */
  const single = selectedRows.length === 1 ? selectedRows[0] : null;

  // ── Actions ─────────────────────────────────────────────────────────────

  const openDetail = (id: DetailTarget, mode: 'view' | 'edit') => {
    setDetailMode(mode);
    setDetail(id);
  };

  const onDelete = async () => {
    if (selectedRows.length === 0) return;

    // Only an admin-created message can be deleted: a legacy row belongs to XAF,
    // which is still running and would re-seed it, and a catalog row's existence
    // is owned by code. Say so BEFORE the confirm rather than letting the API
    // reject it afterwards.
    const blocked = selectedRows.filter((r) => sourceOf(r) !== 'Admin');
    if (blocked.length > 0) {
      notify.warning(
        blocked.length === selectedRows.length
          ? 'Only messages created here can be deleted. Legacy and catalog messages are owned elsewhere.'
          : `${blocked.length} of the selected messages cannot be deleted here, because legacy and catalog messages are owned elsewhere.`,
      );
      return;
    }

    const n = selectedRows.length;
    await runDelete({
      formName: MESSAGE_FORM_NAME,
      message: `Delete ${n} message${n === 1 ? '' : 's'}?`,
      delete: async () => { for (const r of selectedRows) await deleteMessage(r.oid); },
      onDeleted: async () => { setSelected(new Set()); await load(); },
    });
  };

  const onAssign = async (moduleId: string | null, boId: string | null) => {
    const ids = selectedRows.map((r) => r.oid);
    const moduleName = modules.find((m) => m.id === moduleId)?.name ?? '(none)';

    // Every state-changing action goes through the shared centred confirm
    // (standing rule 7).
    const ok = await confirm({
      title: 'Assign Module',
      message: `Set the Module to ${moduleName} for ${ids.length} message${ids.length === 1 ? '' : 's'}?`,
    });
    if (!ok) return;

    setAssignOpen(false);
    const changed = await assignModule(ids, moduleId, boId);
    notify.success(`Assigned ${changed} message${changed === 1 ? '' : 's'} successfully.`);
    setSelected(new Set());
    await load();
  };

  /**
   * Runs the code-catalog sync on demand — the same reconciliation the API does
   * at startup. Offered because a catalog can be deployed without restarting the
   * API, and because seeing the counts is how you confirm a deploy landed.
   *
   * The result is reported in full rather than as "done": an admin needs to know
   * when their reworded message was KEPT over a new code default, and when a key
   * was retired because the code stopped raising it.
   */
  const onSyncCatalog = async () => {
    const ok = await confirm({
      title: 'Sync Catalog',
      message: 'Read the code message catalogs and apply them? '
        + 'Existing wording you have edited is kept, and nothing is ever deleted.',
    });
    if (!ok) return;

    const r = await syncCatalog();
    if (r.noChanges) {
      notify.info(`Catalog sync completed. ${r.catalogEntries} entries, nothing to change.`);
    } else {
      const parts = [
        r.inserted && `${r.inserted} added`,
        r.updated && `${r.updated} updated`,
        r.customizedKept && `${r.customizedKept} kept your wording`,
        r.reactivated && `${r.reactivated} reactivated`,
        r.retired && `${r.retired} retired`,
      ].filter(Boolean);
      notify.success(`Catalog sync completed successfully. ${parts.join(', ')}.`);
    }
    await load();
  };

  /** Downloads the Import template: the rows currently shown, in the import
   *  column order, so an operator edits real wording in Excel rather than
   *  starting from an empty sheet and retyping keys. */
  const downloadTemplate = () => {
    downloadCsv(
      'MessageLibrary-ImportTemplate.csv',
      toCsv(IMPORT_COLUMNS, templateRows(visible)),
    );
    notify.success('Exported to CSV successfully.');
  };

  // ── The detail view's data layer ────────────────────────────────────────

  /** Maps the config's field keys onto the API payload. The read-only fields
   *  (msid / source / status) are display-only and never sent. */
  const adapter: DetailDataAdapter = useMemo(() => ({
    load: async (id: string) => {
      const d = await fetchMessage(id);
      if (!d) return {};
      return {
        msid: d.msid ?? '',
        message: d.message ?? '',
        messageKey: d.messageKey ?? '',
        moduleId: d.moduleId ?? '',
        boId: d.boId ?? '',
        actionType: d.actionType ?? '',
        severity: d.severity ?? '',
        remark: d.remark ?? '',
        navId: d.navId ?? '',
        params: d.params ?? '',
        source: d.source ?? 'Legacy',
        status: d.status ?? 'Active',
        // Via the shared helper — MM/DD/YYYY HH:mm, 24-hour, never inline (§14b.6).
        lastUpdated: fmtLocalDateTime(d.lastUpdatedDate),
        lastUpdatedBy: d.lastUpdatedBy ?? '',
      };
    },
    create: async (payload) => {
      const { id } = await createMessage(toInput(payload));
      // Only now does a parent exist to hang them off. Incomplete rows are
      // dropped rather than written half-filled - both cells are required.
      for (const t of draftTranslationsRef.current.filter(isCompleteDraft)) {
        await addTranslation(id, t.language!.trim(), t.translation!.trim());
      }
      setDraftTranslations([]);
      return id;
    },
    update: async (id: string, payload) => {
      await updateMessage(id, toInput(payload));
    },
  }), []);

  // A NEW message needs a key; an existing one's key is locked unless it collides
  // with another row's, which is the one case that cannot be resolved any other
  // way. The API enforces the same three cases.
  const detailConfig = useMemo(() => {
    if (detail === null) return messageLibraryConfig(vocab, 'new');
    const row = typeof detail === 'string' ? rows.find((r) => r.oid === detail) ?? null : null;
    return messageLibraryConfig(vocab, canEditKey(row, index) ? 'duplicate' : 'locked');
  }, [vocab, detail, rows, index]);

  if (detail !== undefined) {
    // Reference panels under the form. Only for a SAVED record: a message being
    // created is raised nowhere and has no translations yet, so the tabs would
    // have nothing to say.
    // A new message has no usage, no history and nothing to compare against, so
    // those three tabs say so rather than vanishing - a tab that appears only
    // after saving reads as something being broken.
    // ONE list, whether the record exists or not. Every panel takes
    // `detailRecord` (possibly null) and draws its own headed grid, so a tab
    // looks and reads the same before and after the first save.
    //
    // Order follows the prototype: Translations first - it is the only one you
    // ACT on; the other three are readings.
    const extraTabs: DetailExtraTab[] = [
      {
        id: 'translations',
        label: 'Translations',
        count: detailRecord ? detailRecord.translations.length : draftTranslations.length,
        render: () => (
          <TranslationsTab
            messageId={detailRecord?.oid ?? null}
            translations={detailRecord ? detailRecord.translations : draftTranslations}
            languages={vocab.languages}
            onChanged={() => { void reloadDetail(); }}
            onDraftChange={setDraftTranslations}
          />
        ),
      },
      {
        id: 'usedin',
        label: 'Used In',
        count: detailRecord?.usedIn.length ?? 0,
        render: () => <UsedInPanel detail={detailRecord} />,
      },
      {
        id: 'samewording',
        label: 'Same Wording',
        count: sameWordingOf(detailRecord, rows).length,
        render: () => <SameWordingPanel detail={detailRecord} rows={rows} />,
      },
      {
        id: 'history',
        label: 'Change History',
        count: history.length,
        render: () => <ChangeHistoryPanel detail={detailRecord} rows={history} />,
      },
    ];

    return (
      <SettingsDetailPage
        config={detailConfig}
        adapter={adapter}
        id={detail}
        initialMode={detail === null ? 'edit' : detailMode}
        title={navMeta(PAGE_ID)?.label ?? 'Message Library'}
        // From navData, never a literal — the detail's breadcrumb drifted to
        // "System" after the nav entry moved to Toolbox, exactly as the list's
        // did (user report 2026-09-29).
        group={navGroupLabel(PAGE_ID) ?? ''}
        siblingIds={visible.map((r) => r.oid)}
        onHome={() => setDetail(undefined)}
        onNavigate={(id) => openDetail(id, detailMode)}
        onClose={() => setDetail(undefined)}
        onSaved={() => { void load(); }}
        extraTabs={extraTabs}
        // Handed the values as TYPED, so the preview follows the wording and the
        // severity while they are being edited rather than only after a save.
        aside={(values) => (
          <MessagePreview
            message={String(values.message ?? '')}
            severity={(values.severity as Severity) || null}
            suggestion={detailRecord?.severitySuggestion ?? null}
          />
        )}
      />
    );
  }

  // ── Columns ─────────────────────────────────────────────────────────────

  const columns: AppColumn<MessageRow>[] = [
    // MSID is zero-padded TEXT, so it needs a numeric comparator — a string sort
    // is only right while every id is the same width.
    { field: 'msid', header: 'MSID', width: 80, compare: compareMsid },
    { field: 'messageKey', header: 'Message Key', width: 210 },
    { field: 'message', header: 'Message', width: 340 },
    {
      // The module an admin ASSIGNED, else the one the legacy code raises it in —
      // shown muted so the two never read alike. "Shared" means several modules
      // raise it, so no single one owns the wording. A derived value is display
      // only: the record stays unassigned until Assign Module writes one.
      field: 'module',
      header: 'Module',
      width: 150,
      text: (r) => r.module ?? r.derivedModule ?? '',
      render: (r) => {
        if (r.module) return r.module;
        if (r.derivedModule === 'Shared') {
          return <span className="ml-pill info" title="Raised on screens in more than one module">Shared</span>;
        }
        return r.derivedModule
          ? <span className="ml-derived" title="Where the legacy code raises it — not assigned yet">{r.derivedModule}</span>
          : <Muted>Not assigned</Muted>;
      },
    },
    {
      // Same rule as Module: the assigned BO, else the single screen that raises
      // it. Several screens leave this BLANK rather than picking one arbitrarily —
      // no grid in this app shows a dash for missing data either.
      field: 'bo',
      header: 'BO',
      width: 150,
      text: (r) => r.bo ?? r.derivedBo ?? '',
      render: (r) => {
        if (r.bo) return r.bo;
        return r.derivedBo
          ? <span className="ml-derived" title="Where the legacy code raises it — not assigned yet">{r.derivedBo}</span>
          : '';
      },
    },
    {
      // HOW MANY screens raise this message, from the embedded scan of the ALPACA
      // source (scripts/scan-legacy-message-usage.cjs). A count rather than names:
      // the question before rewording is "how many screens does this change?", and
      // the names are one hover away. This reads the CODE, not the database —
      // dbo.MessageEditor does not record it and G6 cannot derive it, because the
      // code that raises these messages is XAF's.
      field: 'usedInScreens',
      header: 'Used In',
      width: 120,
      type: 'number',
      text: (r) => (r.usedInScreens === 0
        ? 'Not used'
        : `${r.usedInScreens} screen${r.usedInScreens === 1 ? '' : 's'}`),
      compare: (a, b) => a.usedInScreens - b.usedInScreens,
      render: (r) => {
        if (r.usedInScreens === 0) return <span className="ml-pill mute">Not used</span>;
        return (
          <span title={`${r.usedInForms.join(', ')} — ${r.usedInSites} call site${r.usedInSites === 1 ? '' : 's'}`}>
            {r.usedInScreens} screen{r.usedInScreens === 1 ? '' : 's'}
          </span>
        );
      },
    },
    { field: 'actionType', header: 'Action Type', width: 150 },
    {
      field: 'severity',
      header: 'Severity',
      width: 130,
      // A SUGGESTION is visibly not a decision — but the difference is opacity and
      // a trailing "?", not a different font: the same pill, dimmed.
      text: (r) => severityOf(r).value,
      render: (r) => {
        const { value, suggested } = severityOf(r);
        return (
          <span
            className={`ml-pill ${SEVERITY_TONE[value] ?? 'info'}${suggested ? ' is-suggested' : ''}`}
            title={suggested ? 'Suggested from the wording — not saved yet' : undefined}
          >
            {value}{suggested ? ' ?' : ''}
          </span>
        );
      },
    },
    {
      field: 'translationCount',
      header: 'Translations',
      width: 120,
      align: 'center',
      type: 'number',
      // The count is the way in: clicking it manages that message's translations.
      // It renders as a plain number like any other cell — the affordance is the
      // tooltip and the hover state, not permanent link styling.
      render: (r) => (
        <button
          type="button"
          className="ml-countcell"
          onClick={() => setTranslationsFor(r)}
          title="Manage translations"
        >
          {r.translationCount}
        </button>
      ),
    },
    {
      field: 'checks',
      header: 'Checks',
      width: 240,
      sortable: false,
      // Filter/sort/export see EVERY check, including the muted attribution ones,
      // so a search for "No module" still finds those rows...
      text: (r) => checksOf(r, index).map((c) => c.label).join(', ') || 'OK',
      // ...but the CELL shows only what needs attention. "No module" and "Not used
      // in code" are facts about attribution, not faults in the message, and each
      // already reachable through the column filter; as chips they put a grey pill on every row
      // and bury the checks that matter.
      render: (r) => {
        const cs = visibleChecks(r, index);
        return cs.length === 0
          ? <span className="ml-pill ok">OK</span>
          : <>{cs.map((c) => <span key={c.label} className={`ml-pill ${c.tone}`}>{c.label}</span>)}</>;
      },
    },
    {
      field: 'source',
      header: 'Source',
      width: 110,
      text: (r) => sourceOf(r),
      render: (r) => <span className={`ml-pill ${SOURCE_TONE[sourceOf(r)]}`}>{sourceOf(r)}</span>,
    },
    {
      field: 'status',
      header: 'Status',
      width: 120,
      text: (r) => statusOf(r),
      render: (r) => <span className={`ml-pill ${STATUS_TONE[statusOf(r)]}`}>{statusOf(r)}</span>,
    },
    {
      field: 'lastUpdatedDate',
      header: 'Last Updated',
      width: 150,
      // Via the shared helper — MM/DD/YYYY HH:mm, 24-hour, never inline (§14b.6).
      text: (r) => fmtLocalDateTime(r.lastUpdatedDate),
      render: (r) => fmtLocalDateTime(r.lastUpdatedDate),
    },
    // Last Updated is the final column, matching the spec's column set. "Updated By"
    // was an addition of mine and is dropped; the value still reaches the client and
    // shows on the detail view, so nothing is lost from the record.
  ];

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area ml-page">
        {/* Breadcrumb above the toolbar, matching the Version Log list page. The
            TITLE is not repeated here: FormTitle stopped rendering an on-page
            heading in BO UI Standard v3 and the title lives in the top bar.

            The group comes from navData, never a literal — this form moved from
            System to Toolbox and a hardcoded trail kept saying System. */}
        <div className="ml-bc">
          <span>Home</span>
          {navGroupLabel(PAGE_ID) && (
            <>
              <i className="ti ti-chevron-right" />
              <span>{navGroupLabel(PAGE_ID)}</span>
            </>
          )}
          <i className="ti ti-chevron-right" />
          <span className="active">{navMeta(PAGE_ID)?.label ?? 'Message Library'}</span>
        </div>

        <FormTitle
          pageId={PAGE_ID}
          loadMs={loadMs}
          loading={loading}
          auditFormName={MESSAGE_FORM_NAME}
        />

        {/* No row count here: the shared grid footer already reports
            "Page X of N (398 items)", and the same number twice on one screen is
            one to keep in step for nothing (user request 2026-09-29). */}

        {/* The standard list-page shell: toolbar row, search row, then the grid
            in a card — the same composition <ModuleListView> uses, so this bespoke
            page sits on the page identically to every config-driven form
            (standing rule 9). */}
        <div className="mlv-toolbar-row">
          <AppToolbar
            left={
              <>
                <button
                  type="button"
                  className="ca-btn"
                  disabled={selectedRows.length === 0}
                  onClick={() => setAssignOpen(true)}
                  title="Set the Module and BO for the selected messages"
                >
                  <span className="tb-i"><i className="ti ti-tag" /></span>
                  Assign Module
                </button>
                <button
                  type="button"
                  className="ca-btn"
                  onClick={() => void onSyncCatalog()}
                  title="Read the code message catalogs and apply them — the same sync the API runs at startup"
                >
                  <span className="tb-i"><i className="ti ti-refresh-dot" /></span>
                  Sync Catalog
                </button>
              </>
            }
            onNew={() => openDetail(null, 'edit')}
            onEdit={() => single && openDetail(single.oid, 'edit')}
            editDisabled={!single}
            onDelete={() => void onDelete()}
            deleteDisabled={selectedRows.length === 0}
            onRefresh={() => void load()}
            refreshing={loading}
            onImport={() => setImportOpen(true)}
            onTemplate={downloadTemplate}
            exportData={{
              filename: 'Message Library',
              headers: columns.map((c) => (typeof c.header === 'string' ? c.header : c.headerText ?? c.field)),
              rows: visible.map((r) => columns.map((c) =>
                c.text ? c.text(r) : String((r as unknown as Record<string, unknown>)[c.field] ?? ''))),
            }}
          />
        </div>

        <GridSearch value={search} onChange={setSearch} placeholder="Search MSID, key, message, module…" />

        <div className="bl-card bl-card-flex">
          <AppDataGrid<MessageRow>
            rows={visible}
            columns={columns}
            getRowId={(r) => r.oid}
            loading={loading}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            onRowDoubleClick={(r) => openDetail(r.oid, 'view')}
            initialSort={{ key: 'msid', dir: 'asc' }}
            defaultPageSize={20}
            // The toolbar already carries "Export to", so the grid's own header
            // cluster is suppressed rather than showing a second export control.
            hideHeaderTools
            emptyText="No messages"
          />
        </div>
      </div>

      <AssignModuleDialog
        open={assignOpen}
        count={selectedRows.length}
        modules={modules}
        onApply={(moduleId, boId) => void onAssign(moduleId, boId)}
        onClose={() => setAssignOpen(false)}
      />

      <ImportMessagesDialog
        open={importOpen}
        rows={rows}
        onClose={() => setImportOpen(false)}
        onImported={() => { void load(); }}
      />

      <TranslationsDialog
        message={translationsFor}
        languages={vocab.languages}
        onClose={() => setTranslationsFor(null)}
        onChanged={() => { void load(); }}
      />
    </div>
  );
}

/** Field keys → the API payload. Read-only display fields are never sent. */
function toInput(values: Record<string, unknown>) {
  const s = (k: string) => {
    const v = values[k];
    return v === undefined || v === null || v === '' ? null : String(v);
  };
  return {
    messageKey: s('messageKey'),
    message: s('message'),
    severity: s('severity'),
    moduleId: s('moduleId'),
    boId: s('boId'),
    actionType: s('actionType'),
    remark: s('remark'),
    navId: s('navId'),
    params: s('params'),
  };
}

const SEVERITY_TONE: Record<string, string> = {
  Error: 'alert', Warning: 'warn', Confirm: 'info', Info: 'info', Success: 'ok',
};
const SOURCE_TONE: Record<string, string> = {
  Legacy: 'mute', Catalog: 'info', Admin: 'alert',
};
const STATUS_TONE: Record<string, string> = {
  Active: 'ok', Customized: 'warn', Retired: 'mute',
};

/** Secondary microcopy uses the ONE shared muted token (§18.6). */
function Muted({ children }: { children: React.ReactNode }) {
  return <span className="ml-muted">{children}</span>;
}

export default MessageLibraryPage;
